import * as API from "../api"
import { getAuthToken, getAuthType } from "../util"

/**
 * Typed client for the Autodesk company/hub self-provisioning endpoints (WA-9395).
 *
 * Wraps the JS API layer (capn/assets/js/api) with the response/request shapes the
 * setup wizard works with. See capn/api_v4/viewsets/autodesk_provisioning.py.
 */

/** A Rhumbix company already linked to an Autodesk context. */
export interface LinkedCompany {
    id: number
    name: string
    hub_urn: string | null
    is_active: boolean
}

/** A Rhumbix licensable product (RMBX / RMBXTM) on a team, with the user's admin status. */
export interface EligibleProduct {
    feature_id: string
    /** True when the user administers this product on the team (may create/link). */
    is_team_admin: boolean
    /** Human-readable product name from the Admin Graph (e.g. "Rhumbix"). Empty when unavailable. */
    product_name: string
}

/** A Rhumbix-eligible Autodesk subscriber context (team) for the logged-in user. */
export interface EligibleContext {
    subscriber_context_id: string
    /** Human-readable team name from the Admin Graph. Empty when unavailable. */
    team_name: string
    products: EligibleProduct[]
    linked_companies: LinkedCompany[]
}

/** A company the user administers that is not yet Autodesk-linked (case (a) candidate). */
export interface LinkableCompany {
    id: number
    name: string
}

/** Response of the eligible-contexts endpoint; drives post-login routing. */
export interface EligibleContextsResponse {
    has_company: boolean
    /** The logged-in user's id + email — used to re-seed the shell on reload. */
    user_id: number
    email: string
    is_autodesk_user: boolean
    contexts: EligibleContext[]
    linkable_companies: LinkableCompany[]
    /**
     * Present when Autodesk team discovery (eligibleTeams/entitlementPools) failed —
     * contexts is [] in that case, but linkable_companies/has_company still reflect
     * real data (plain DB reads unaffected by the Admin Graph failure). Show as a
     * non-blocking notice rather than treating the response as empty/absent.
     */
    contexts_error?: string
}

/** An existing Rhumbix hub on a team, for case (a) hub selection. */
export interface AutodeskHub {
    id: string
    name: string | null
}

/** The company fields returned after a create/link, used to re-bootstrap the app. */
export interface ProvisionedCompany {
    id: number
    company_name: string
    hub_urn: string | null
    autodesk_feature_id: string | null
    is_active: boolean
    /** Fresh Knox token scoped to the employee created/found for this company. */
    token?: string
    /** Employee id matching the issued token, for per-employee token caching. */
    employee_id?: number
}

/** Body for creating a new company + hub (cases (b)/(c)). */
export interface CreateCompanyInput {
    subscriber_context_id: string
    feature_id: string
    company_name: string
    /**
     * When set, use this existing Autodesk hub instead of provisioning a new one.
     * region and description are ignored in this path (the hub already exists).
     */
    hub_urn?: string
    region?: string
    description?: string
    start_of_week?: string
    role?: string
    address?: string
    /** Optional company logo image; sent as multipart when present. */
    logo?: File
}

/** Body for linking an existing admin company to a context/hub (case (a)). */
export interface LinkCompanyInput {
    subscriber_context_id: string
    feature_id: string
    company_id: number
    /** When set, link the company to this existing hub URN. Mutually exclusive with new-hub fields. */
    hub_urn?: string
    /**
     * When set (and hub_urn is absent), provision a new hub for the company.
     * region and description apply only to the new-hub path.
     */
    region?: string
    description?: string
    /** Hub name for new hub provisioning; defaults to the company name on the server. */
    hub_name?: string
}

/**
 * Fetch the Rhumbix-eligible Autodesk contexts (and routing signal) for the user.
 *
 * @returns The eligible-contexts payload, including has_company.
 */
export async function fetchEligibleContexts(): Promise<EligibleContextsResponse> {
    return API.getAutodeskEligibleContexts()
}

/**
 * Fetch the existing Rhumbix hubs on a context the user is entitled to (case (a)).
 *
 * @param subscriberContextId - The Autodesk context (teamId) to list hubs for.
 * @returns The hubs wrapper for the context.
 */
export async function fetchHubs(subscriberContextId: string): Promise<{ hubs: AutodeskHub[] }> {
    return API.getAutodeskHubs(subscriberContextId)
}

/**
 * Create a new Rhumbix company plus its Autodesk hub (cases (b)/(c)).
 *
 * Sent as multipart/form-data so the optional logo image can be uploaded with the form.
 * The shared JSON API client forces a JSON content-type (which would break the multipart
 * boundary), so this uses a direct fetch with the stored auth token (Token auth needs no
 * CSRF). Other calls stay on the JSON client.
 *
 * @param input - The new company's context, chosen product, name, and optional fields/logo.
 * @returns The provisioned company.
 * @throws Error when the request fails (non-2xx); the message is the server's when available.
 */
export async function createCompany(input: CreateCompanyInput): Promise<ProvisionedCompany> {
    const form = new FormData()
    form.append("subscriber_context_id", input.subscriber_context_id)
    form.append("feature_id", input.feature_id)
    form.append("company_name", input.company_name)
    if (input.hub_urn) form.append("hub_urn", input.hub_urn)
    if (input.region) form.append("region", input.region)
    if (input.description) form.append("description", input.description)
    if (input.start_of_week) form.append("start_of_week", input.start_of_week)
    if (input.role) form.append("role", input.role)
    if (input.address) form.append("address", input.address)
    if (input.logo) form.append("logo", input.logo)

    const token = getAuthToken()
    const headers: Record<string, string> = { Accept: "application/json" }
    if (token) {
        headers.Authorization = `${getAuthType()} ${token}`
    }

    const response = await fetch("/api/v4/autodesk/companies/", {
        method: "POST",
        headers,
        body: form,
    })
    if (!response.ok) {
        let message = "Failed to create company."
        try {
            const data = await response.json()
            if (data?.message) message = data.message
        } catch {
            // Non-JSON error body; fall back to the default message.
        }
        throw new Error(message)
    }
    return response.json()
}

/**
 * Link an existing company the user administers to an Autodesk context/hub (case (a)).
 *
 * Uses a direct fetch (not the shared JSON client) so non-2xx responses surface
 * the server's error message rather than a generic HTTP status string.
 *
 * @param input - The context, target company id, and optional chosen hub.
 * @returns The linked company.
 * @throws Error when the request fails (non-2xx); the message is the server's when available.
 */
export async function linkCompany(input: LinkCompanyInput): Promise<ProvisionedCompany> {
    const token = getAuthToken()
    const headers: Record<string, string> = {
        Accept: "application/json",
        "Content-Type": "application/json",
    }
    if (token) {
        headers.Authorization = `${getAuthType()} ${token}`
    }
    const body: Record<string, string | number> = {
        subscriber_context_id: input.subscriber_context_id,
        feature_id: input.feature_id,
        company_id: input.company_id,
    }
    if (input.hub_urn) body.hub_urn = input.hub_urn
    if (input.region) body.region = input.region
    if (input.description) body.description = input.description
    if (input.hub_name) body.hub_name = input.hub_name

    const response = await fetch("/api/v4/autodesk/companies/link/", {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    })
    if (!response.ok) {
        let message = "Failed to link company."
        try {
            const data = await response.json()
            if (data?.message) message = data.message
        } catch {
            // Non-JSON error body; fall back to the default message.
        }
        throw new Error(message)
    }
    return response.json()
}
